/**
 * CORE-1681 — ссылка уведомления «в чаты мастера».
 *
 * Бэкенд (CORE-1704) строит переход об упоминании, об ответе и о завершении
 * эфира как `/u/{masterUserId}#communityChat/{discussionId|general}[/{messageId}]`.
 * Id сообщения идёт третьим сегментом хэша, а не `?message=`: выпущенные
 * клиенты делят хэш по «/», и у них ссылка по-прежнему открывает обсуждение.
 *
 * Разбор ручной, без `URL` (у полифилла React Native `hash` не везде есть) и
 * без зависимостей от RN — проверяется под jest. Тем же разбором живёт и
 * панель чата: хэш в адресе дашборда — это та же ссылка без хоста и пути.
 */
/**
 * CORE-1654 — второй сегмент `#communityChat/deleted`: обсуждения больше нет,
 * и вкладка открывается сразу в состоянии «Обсуждение удалено», без запроса.
 * Сюда ведёт «Обсудить» с карточки события, чьё обсуждение удалил мастер: id
 * у карточки уже нет, а вести в общий чат — значит выдать удаление за пустоту.
 */
export const DELETED_DISCUSSION_ROUTE = 'deleted';

export interface CommunityChatHash {
  /** `general` — общий чат, иначе id обсуждения (`core619-…`). */
  discussionId: string;
  /** Сообщение, к которому вести и которое выделить. */
  messageId: string | null;
}

export interface CommunityChatLink extends CommunityChatHash {
  masterUserId: string;
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** `#communityChat/<id>[/<messageId>]` (с решёткой или без) → части; иначе null. */
export function parseCommunityChatHash(raw: unknown): CommunityChatHash | null {
  if (typeof raw !== 'string') return null;
  const [tab, id, message] = raw.trim().replace(/^#/, '').split('/');
  if (tab !== 'communityChat' || !id) return null;
  const messageId = message ? decode(message) : null;
  return { discussionId: decode(id), messageId: messageId && messageId.trim() ? messageId : null };
}

// Схема и хост необязательны: бэкенд кладёт и абсолютные, и относительные.
const COMMUNITY_CHAT_LINK = /^(?:[a-z][a-z0-9+.-]*:\/\/[^/?#]*)?\/u\/([^/?#]+)\/?(?:\?[^#]*)?#(.*)$/i;

/** `/u/{masterUserId}#communityChat/…` → кого открыть и что выделить; иначе null. */
export function parseCommunityChatLink(raw: unknown): CommunityChatLink | null {
  if (typeof raw !== 'string') return null;
  const match = COMMUNITY_CHAT_LINK.exec(raw.trim());
  if (!match) return null;
  const hash = parseCommunityChatHash(match[2]);
  if (!hash) return null;
  return { masterUserId: decode(match[1]), ...hash };
}
